import { useCallback, useMemo } from 'react';
import { IStudent, IStudentAgreement } from 'types';
import {
  usePaginatedStudentsQuery,
  useMetaStudentsQuery,
  StudentEdge,
  PaginatedStudentsQueryVariables,
  PageInfo,
  AgreementType,
} from 'core/graphql';
import { handleFinancialNumberValue } from 'services/NumberService';

type StudentsHookType = {
  data: IStudent[];
  loading?: boolean;
  error?: string;
  totalCount?: number | null;
  agreementsCount?: number | null;
  pageInfo?: Pick<
    PageInfo,
    'startCursor' | 'endCursor' | 'hasNextPage' | 'hasPreviousPage'
  >;
  operations: {
    fetchMoreData: (vars: PaginatedStudentsQueryVariables) => void;
  };
};

// O(n) with a Set + push (spec 0008 round 2): the old spread-in-reduce with
// Array.includes was O(n²) and re-ran on EVERY render — the filter-drain of
// the whole roster made the Students page freeze while typing.
const removeDuplicates = (data: IStudent[]): IStudent[] => {
  const result: IStudent[] = [];
  const takenContracts = new Set<string>();
  data.forEach((student) => {
    if (
      student.contractTitle !== '' &&
      takenContracts.has(student.contractTitle)
    ) {
      return;
    }
    result.push(student);
    if (student.contractTitle !== '') takenContracts.add(student.contractTitle);
  });
  return result;
};

const useGetStudentsFromApi = () => {
  const { data, loading, error, fetchMore } = usePaginatedStudentsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      first: 10,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: metadata, fetchMore: fetchMoreMeta } = useMetaStudentsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      query: null,
    },
    notifyOnNetworkStatusChange: true,
  });

  // Memoized on the edges + O(n) flatten (spec 0008 round 2): the old
  // `result()` re-built the whole roster array (with O(n²) spreads) on every
  // render of every consumer.
  const result = useMemo<IStudent[]>(() => {
    const edges = data?.allStudents?.edges;
    if (!edges) {
      return [];
    }
    const flat: IStudent[] = [];
    edges.forEach((edge) => {
      flat.push(...toIStudentArray(edge as StudentEdge));
    });
    return removeDuplicates(flat);
  }, [data?.allStudents?.edges]);

  return {
    fetchMore,
    fetchMoreMeta,
    result,
    loading,
    error,
    pageInfo: data?.allStudents?.pageInfo,
    totalCount: metadata?.metaStudents?.totalCount || 0,
    agreementsCount: metadata?.metaStudents?.agreementsCount || 0,
  };
};

const toIStudentArray = (edge: StudentEdge | null): IStudentAgreement[] => {
  if (!edge) return [];
  const { node } = edge;
  if (node && node.user) {
    const { agreements, user } = node;

    if (!agreements.length) {
      const { firstName, lastName, middleName, email, phone } = user;

      return [
        {
          id: node.id,
          studentId: node.id,
          firstName,
          lastName,
          middleName: middleName || '',
          email,
          name: `${lastName} ${firstName} ${middleName || ''}`,
          contractTitle: '',
          phone: phone || '',
          budgetAmount: '',
          paidAmount: '',
          group: '',
          terminated: false,
          isLmsBlocked: Boolean(node.isLmsBlocked),
        },
      ];
    }

    // Plain map — the old `[...acc, student]` spread-in-reduce was O(n²).
    return agreements.map(
      ({
        agreementTitle: contractTitle,
        currency,
        terminated,
        budgetAmount,
        paidAmount,
        id: agreementId,
        group: { name: groupName },
      }: AgreementType): IStudentAgreement => {
        const { firstName, lastName, middleName, email } = user;
        return {
          id: node.id,
          agreementId,
          studentId: node.id,
          firstName,
          lastName,
          middleName: middleName || '',
          email,
          name: `${lastName} ${firstName} ${middleName || ''}`,
          contractTitle,
          group: groupName,
          phone: user?.phone || '',
          budgetAmount: `${handleFinancialNumberValue(
            budgetAmount,
          )} ${currency}`,
          paidAmount: `${handleFinancialNumberValue(paidAmount)} ${currency}`,
          terminated,
          // Raw numeric values + currency threaded for the debt column,
          // sorting, filtering and export (Area 1). The formatted strings
          // above are retained for legacy cells; the new debt-aware cells read
          // these raw fields. `Number(...)` coerces Django's string decimals.
          budgetRaw: Number(budgetAmount) || 0,
          paidRaw: Number(paidAmount) || 0,
          currency: currency || '',
          isLmsBlocked: Boolean(node.isLmsBlocked),
        };
      },
    );
  }
  return [];
};

export const useStudents = (): StudentsHookType => {
  const {
    result,
    loading,
    totalCount,
    agreementsCount,
    fetchMore = () => null,
    fetchMoreMeta = () => null,
    pageInfo,
  } = useGetStudentsFromApi();

  const fetchMoreData = useCallback(
    ({ query, after }: PaginatedStudentsQueryVariables) => {
      fetchMore({
        variables: {
          query,
          after,
        },
      });

      fetchMoreMeta({
        variables: {
          query,
        },
      });
    },
    [fetchMore, fetchMoreMeta],
  );

  return {
    data: result || [],
    loading,
    pageInfo,
    totalCount,
    agreementsCount,
    operations: {
      fetchMoreData,
    },
  };
};
